import { Injectable } from '@angular/core'
import paper from 'paper/dist/paper-core'
import {
  CycleDimensions,
  CycleDimensionValuesForCalculation,
} from '../../../models/cycleDimensions'

interface WallInfo {
  id: number
  length: number
  isEdge: boolean
  points: paper.Point[]
  orientation: 'horizontal' | 'vertical'
}

interface Curve {
  length: number
  points: paper.Point[]
}

@Injectable()
export class CycleDimensionsService {
  constructor() {}

  public calculateCycleInformation(
    cycleDimensionValuesForCalculation: CycleDimensionValuesForCalculation[],
    wallHeight: number
  ): CycleDimensions[] {
    const cycleDimensions: CycleDimensions[] = []

    cycleDimensionValuesForCalculation.forEach((cycleDimensionValueForCalculation) => {
      const cycleDimension = {} as CycleDimensions
      cycleDimension.cycleNumber = cycleDimensionValueForCalculation.cycleNumber
      cycleDimension.length = cycleDimensionValueForCalculation.length ?? 0

      if (wallHeight) {
        const curCircumference = cycleDimensionValueForCalculation.circumference
        if (curCircumference) {
          // 2. calculate cm2
          cycleDimension.area = curCircumference * wallHeight
        }
      }

      // 3. calculate cm3 volume
      const curArea = cycleDimensionValueForCalculation.floorArea
      if (wallHeight && curArea) {
        cycleDimension.volume = curArea * wallHeight
      }

      cycleDimensions.push(cycleDimension)
    })

    cycleDimensions.sort((a, b) => a.cycleNumber - b.cycleNumber)
    return cycleDimensions
  }

  public getLength(curves: { length: number; points: paper.Point[] }[]): number {
    const interpretedWalls = this.interpretWallsFromLines(curves)
    const wallsWithoutEdges = interpretedWalls.filter((wall) => !wall.isEdge)

    return wallsWithoutEdges.reduce((acc, curr) => acc + curr.length, 0) / 2
  }

  // technicalls it is a curve but in our case it is always a straight line
  private interpretWallsFromLines(curves: Curve[]): WallInfo[] {
    const walls: WallInfo[] = []

    // check for orientation
    curves.forEach((curve, index) => {
      const orientation = this.determineOrientation(curve.points)
      walls.push({
        id: index,
        length: curve.length,
        isEdge: false,
        points: curve.points,
        orientation,
      })
    })

    // define width & length
    const allLengths = walls.map((w) => Math.round(w.length))
    const uniqueLengths = [...new Set(allLengths)].sort((a, b) => a - b)

    if (uniqueLengths.length >= 2) {
      const width = uniqueLengths[0]

      walls.forEach((wall) => {
        wall.isEdge = Math.round(wall.length) === width
      })
    } else if (uniqueLengths.length === 1) {
      // if all walls are the same length
      walls.forEach((wall) => {
        // horizontal walls are treated as length
        wall.isEdge = wall.orientation === 'vertical'
      })
    }

    return walls
  }

  private determineOrientation(points: paper.Point[]): 'horizontal' | 'vertical' {
    // take the first and last point for orientation
    const dx = Math.abs(points[0].x - points[points.length - 1].x)
    const dy = Math.abs(points[0].y - points[points.length - 1].y)

    return dx > dy ? 'horizontal' : 'vertical'
  }
}
